import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { Result } from "better-result";
import {
  getKlasLeerlijnAverages,
  getKlasSociaalEmotioneelThemaDomeinAverages,
} from "#/modules/klassen.statistics/api.statistics.server";
import { getClassesForStartLesson } from "#/modules/school.klassen/api.klassen.server";
import { isApiFlagsEnumValue } from "#/modules/util.helpers/zod-enum";
import { Leerjaar } from "#/schema.nswag";

// A combinatieklas offers its combined leerjaar ("Vijf, Zes"); the API resolves it to both years.
const isLeerjaar = isApiFlagsEnumValue(Leerjaar);

interface GetStatisticsInput {
  klasId?: string;
  leerjaar?: string;
}

export const getStatistics = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: GetStatisticsInput) => data)
  .handler(async ({ data }) => {
    const { klasId, leerjaar } = data;
    const leerjaarValue = leerjaar && isLeerjaar(leerjaar) ? leerjaar : null;

    const klassenResult = await getClassesForStartLesson();
    if (Result.isError(klassenResult)) throw klassenResult.error;
    const klassen = klassenResult.value.data ?? [];

    // Resolve the effective class server-side: an explicit selection wins, otherwise default to the
    // first class. Previously this branch `throw redirect`ed to add `?klasId=<first>` to the URL,
    // which is not cacheable during preload and caused a preload redirect-chase storm. We now resolve
    // the default here and return it as `selectedKlasId`; the URL only changes on explicit selection.
    const effectiveKlasId = klasId ?? (klassen[0] ? klassen[0].id.toString() : undefined);

    let leerlijnAveragesData = null;
    let sociaalEmotioneelThemaDomeinAveragesData = null;

    // Fetch the averages in parallel for the effective class.
    if (effectiveKlasId) {
      const [leerlijnResult, sociaalResult] = await Promise.all([
        getKlasLeerlijnAverages(effectiveKlasId, leerjaarValue),
        getKlasSociaalEmotioneelThemaDomeinAverages(effectiveKlasId, leerjaarValue),
      ]);

      if (Result.isError(leerlijnResult)) throw leerlijnResult.error;
      if (Result.isError(sociaalResult)) throw sociaalResult.error;

      leerlijnAveragesData = leerlijnResult.value;
      sociaalEmotioneelThemaDomeinAveragesData = sociaalResult.value;
    }

    return {
      klassen,
      leerlijnAveragesData,
      sociaalEmotioneelThemaDomeinAveragesData,
      selectedKlasId: effectiveKlasId ?? null,
    };
  });
